/**
 * Static TypeScript shapes for the lecture entities the public site renders.
 *
 * As of 2026-10-12 the actual lecture data lives in the `cms_lectures` MySQL
 * table; the front-end reads it through `cmsLectures` (regenerated into
 * `src/content/cms/lectures.generated.ts` by `npm run cms:sync`). The empty
 * `LECTURES` array below is a no-op fallback that only fires if the CMS
 * returns an empty list at boot — useful for `FEATURED_LECTURE` and
 * `findLectureBySlug` so they always have a defined shape.
 *
 * To add a new lecture: create it in the CMS admin (or via SQL), then run
 * `npm run cms:sync` so the generated module picks it up. There is no need
 * to touch this file.
 *
 * Audience is one of:
 *   - "teacher"  → 教師場
 *   - "parent"   → 家長場
 *   - "student"  → 學生場
 */

export type LectureAudience = 'teacher' | 'parent' | 'student'

export interface Lecture {
  /** URL-safe identifier, e.g. "yck-20260911" */
  slug: string
  /** ISO 8601 publication / event date (YYYY-MM-DD) */
  date: string
  /** Audience segment — drives the i18n detail.tag* badge */
  audience: LectureAudience
  /** Speaker name (typically the same across the whole series) */
  speaker: string
  /** Host / organiser in Chinese, e.g. 《香港01》 */
  hostHans?: string
  hostZh: string
  /** Host / organiser in English, e.g. HK01 */
  hostEn: string
  /** Venue in Chinese */
  venueHans?: string
  venueZh: string
  /** Venue in English */
  venueEn: string
  /**
   * Official website of the venue / host organisation, e.g.
   * "https://www.yckmc.edu.hk/".
   *
   * Added 2026-10-20. When set, the venue name on a lecture card and on
   * the lecture detail page renders as an external link (target=_blank).
   * Undefined / empty → the venue renders as plain text.
   */
  venueUrl?: string
  /** Series name (parent lectures share one series; this is the teacher session) */
  seriesHans?: string
  seriesZh: string
  /** Series name in English */
  seriesEn: string
  /** Optional external source URL — e.g. HK01 article, if any */
  externalUrl?: string

  // ──────────────────────────────────────────────────────────────────────
  // handoff-1 ✅ filled — Option B (problem-led) chosen 2026-09-16.
  // ──────────────────────────────────────────────────────────────────────
  /** Simplified-Chinese title. Falls back to titleZh when absent. */
  titleHans?: string
  titleZh: string
  titleEn: string
  /** 2-sentence Chinese summary — shown on archive card + news teaser */
  summaryHans?: string
  summaryZh: string
  /** 2-sentence English summary */
  summaryEn: string

  // Photo 1 (primary — used on archive card thumb + detail page top)
  photo1: string
  photo1Thumb: string
  photo1Width: number
  photo1Height: number
  photo1AltZh: string
  photo1AltHans: string
  photo1AltEn: string

  // Photo 2 (secondary — used only on detail page, below the body)
  photo2: string
  photo2Thumb: string
  photo2Width: number
  photo2Height: number
  photo2AltZh: string
  photo2AltHans: string
  photo2AltEn: string

  // SEO overrides (per-locale). When set, <SEO title=.../description=...>
  // prefers them over `title*` / `summary*` so operators can write
  // search-engine-friendly snippets that fit the [20,80] title and
  // [50,180] description thresholds without rewriting the human copy.
  // Added 2026-10-17 to clear audit partials on /en/lectures/<slug>.
  metaTitleZh?: string
  metaTitleHans?: string
  metaTitleEn?: string
  metaDescriptionZh?: string
  metaDescriptionHans?: string
  metaDescriptionEn?: string
}

/**
 * Static fallback list — empty as of R-shift (2026-10-12): every lecture is
 * now in `cms_lectures` (rendered via `lectures.generated.ts`). This array
 * remains as a no-op fallback for `displayedLectures` / `FEATURED_LECTURE`
 * in the rare case the CMS is empty at boot.
 */
export const LECTURES: Lecture[] = []

/** Most recent lecture — what the homepage news-teaser deep-links to. */
export const FEATURED_LECTURE: Lecture | undefined = LECTURES[0]
